import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { getAppConfigProvider } from '@madeinlune/ngx-app-config';
import { environment } from '../environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ScullyLibModule } from '@scullyio/ng-lib';
import { getBrowserLang, TranslocoService } from '@ngneat/transloco';
import { Observable, of, tap } from 'rxjs';
import { StoreModule } from '@ngrx/store';
import { metaReducers, reducers } from './reducers';
import { Router, RouterModule } from '@angular/router';
import { LOCATION } from '@ng-web-apis/common';
import { TranslocoRootModule } from './transloco-root.module';
import { NgrxDrupalPathsToRoutesModule } from '@madeinlune/ngrx-drupal-paths-to-routes';
import { IcilaLuneDrupalToAngularRoutePipe } from '@icilalune/shell-feature/route-drupal';
import { EffectsModule } from '@ngrx/effects';
import { PwaManifestModule } from '@madeinlune/ngx-pwa-manifest';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SHELL_FEATURE_ENVIRONMENT } from '@icilalune/shell-feature/tokens';
import * as Sentry from '@sentry/angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NotFoundInterceptor } from './not-found.interceptor';
import {
  MAINTENANCE_ERROR,
  PATH_NOT_FOUND_ERROR,
} from '@icilalune/shell-state/tokens';
import { ErrorsService } from './errors.service';
import { IcilaluneInternationalizationModule } from '@nx-icilalune/icilalune/internationalization';

function initializeTransloco(
  translocoService: TranslocoService,
  location: Location
): () => Observable<any> {
  return () =>
    of(null).pipe(
      tap(() => {
        const browserLang: string | undefined = getBrowserLang();
        const urlLanguage: string | undefined =
          location.pathname.split('/')?.[1];
        if ((!urlLanguage && browserLang === 'fr') || urlLanguage === 'fr') {
          translocoService.setActiveLang('fr');
        } else {
          translocoService.setActiveLang('en');
        }
      })
    );
}

export const storeDevToolsImport = [
  environment.production ? StoreDevtoolsModule.instrument() : [],
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserAnimationsModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([]),
    ...storeDevToolsImport,
    ScullyLibModule.forRoot({
      useTransferState: true,
      manualIdle: true,
      alwaysMonitor: false,
    }),
    RouterModule.forRoot([
      {
        path: '',
        loadChildren: async () =>
          (await import('@icilalune/shell-feature'))
            .IcilaluneShellFeatureModule,
      },
    ]),
    TranslocoRootModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    NgrxDrupalPathsToRoutesModule.forRoot(
      ['fr', 'en'],
      `${environment.config.api.baseURL}/router/translate-path`,
      IcilaLuneDrupalToAngularRoutePipe,
      true
    ),
    PwaManifestModule.forRoot(),
    IcilaluneInternationalizationModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('highlightjs-line-numbers.js'), // Optional, only if you want the line numbers
        languages: {
          typescript: () => import('highlight.js/lib/languages/typescript'),
          css: () => import('highlight.js/lib/languages/css'),
        },
      },
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [TranslocoService, LOCATION],
      useFactory: initializeTransloco,
    },
    getAppConfigProvider(environment.config),
    {
      provide: SHELL_FEATURE_ENVIRONMENT,
      useValue: {
        production: environment.production,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NotFoundInterceptor,
      multi: true,
    },
    {
      provide: PATH_NOT_FOUND_ERROR,
      deps: [ErrorsService],
      useFactory: (errorsService: ErrorsService) => {
        return errorsService.notFoundError$;
      },
    },
    {
      provide: MAINTENANCE_ERROR,
      deps: [ErrorsService],
      useFactory: (errorsService: ErrorsService) => {
        return errorsService.maintenanceError$;
      },
    },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}
